/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CELL_TEXT_PRIMARY_CLASS,
  CELL_TEXT_RIGHT_OVERFLOW_CLASS,
  code3Formatter,
  conditionalRowPostCreateDecorator,
  createDefaultGridModel,
  defaultCurrencyCellRendererDecorator,
  falsyAsEmptyCellRenderer,
  falsyCellRenderer,
  innerLabelCellRendererDecorator,
  noneCellRenderer,
  rendererIconByName,
  ROW_DIRTY_CLASS,
  ROW_FOOTER_LIGHT_CLASS,
  textOverlayRowPostCreate,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { getRouteInitData, IS_REGION_AU_OR_NZ, NumberTypes, TextTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableMultilineTextColumn,
  buildEditableNumberColumn,
  buildNumberColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  ManagedGridColumn,
  rowDragMoveEventArrayFactory,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { ValueFormatterParams } from 'ag-grid-community';
import { isEmpty, isNil } from 'lodash';
import { Observable } from 'rxjs';
import {
  getJobCoElementId,
  isJobCoElement,
  isJobCoElementTotal,
  JobCoElement, jobCoElementArr,
  jobCoElementFieldState,
  JobCoElementOrTotal,
  JobCoElementTotal,
  jobCoElementTotal,
  validateJobCoElementField,
} from '../../../resources/job-co-details.model';
import { getJobSpecQuoteChangeId, jobSpecQuoteChangeArr } from '../../../resources/job.model';

@Component({
  selector: 'gh-job-co-details-element-list',
  template: require('./job-co-details-element-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCoDetailsElementListComponent implements OnInit {
  @Input() editable$: Observable<boolean>;
  @Input() hasPriceBook: boolean;
  @Input() focusedCell: FocusedCell;

  @Output() add = new EventEmitter<void>();
  @Output() remove = new EventEmitter<JobCoElement>();
  @Output() moveCreditDebit = new EventEmitter<JobCoElement>();
  @Output() viewMemo = new EventEmitter<JobCoElement>();
  @Output() setField = new EventEmitter<{ entity: JobCoElement; field: string; value: any }>();
  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() dragChanges = new EventEmitter<JobCoElement[]>();
  @Output() search = new EventEmitter<string>();
  @Output() clone = new EventEmitter<JobCoElement>();

  gridDefinition: GridDefinition;
  isAuOrNzRegion = IS_REGION_AU_OR_NZ;

  _elements: JobCoElement[];
  _total: JobCoElementTotal[];

  @Input()
  set elements(elements: JobCoElement[]) {
    this._elements = elements;
    this._total = [jobCoElementTotal(elements)];
  }

  constructor(private route: ActivatedRoute,
              private translationService: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService) {
  }

  canMoveToDebit({ isCredit, price }: JobCoElement): boolean {
    return isCredit && price > 0;
  }

  canMoveToCredit({ isCredit, price }: JobCoElement): boolean {
    return !isCredit && price > 0;
  }

  hasMemo(element: JobCoElement): boolean {
    return !isEmpty(element.memo);
  }

  isElement(element: JobCoElement): boolean {
    return !isJobCoElementTotal(element);
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();
  }

  private buildGridDefinition(): GridDefinition {
    let layout = getRouteInitData(this.route)['jobCoElementGridLayout'];
    
    if (!this.isAuOrNzRegion) {
      layout.update({
        columns: layout.columns.filter(
          (item: ManagedGridColumn) => item.column !== 'customerPrice'
        )
      });
    }

    const model = createDefaultGridModel({
      getId: getJobCoElementId,
      editable: this.editable$,
      validate: validateJobCoElementField,
      getCellDef: jobCoElementFieldState,
      command: (entity, field, value) => this.setField.emit({ entity, field, value }),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      layout,
      model,
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: JobCoElement }) => !isNil(data.dirtyType),
          [ROW_FOOTER_LIGHT_CLASS]: ({ data }: { data: JobCoElementOrTotal }) => isJobCoElementTotal(data),
        },
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveChanges.bind(this)),
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobCoElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      },
      columns: {
        'code': {
          rowDrag: true,
        },
        'description': {
          ...buildEditableMultilineTextColumn(
            TextTypes.Text,
            model.getCellAccessor('description'),
            { minRows: 3, maxRows: 10 }),
          pinnedRowCellRenderer: noneCellRenderer,
          cellClass: CELL_TEXT_PRIMARY_CLASS,
        },
        'costCenterCode': {
          valueFormatter: code3Formatter,
          cellClass: 'text-right',
          pinnedRowCellRenderer: noneCellRenderer,
        },
        'price': buildNumberColumn(NumberTypes.Price),
        'orderQuantity': {
          ...buildNumberColumn(NumberTypes.Quantity),
          cellRenderer: falsyAsEmptyCellRenderer,
        },
        'origQuantity': {
          ...buildNumberColumn(NumberTypes.Quantity),
          cellRenderer: falsyAsEmptyCellRenderer,
        },
        'quantity': {
          ...buildEditableNumberColumn(NumberTypes.SignedQuantity, model.getCellAccessor('quantity')),
          pinnedRowCellRenderer: noneCellRenderer,
          cellClass: CELL_TEXT_PRIMARY_CLASS,
        },
        'debit': {
          valueGetter: ({ data }: { data: JobCoElementOrTotal }) =>
            isJobCoElementTotal(data) ? data.debit : (data.isCredit ? void 0 : data.priceExtension),
          ...buildNumberColumn(NumberTypes.Price),
          headerName: this.isAuOrNzRegion
            ? this.translationService.get('lbl_debit_dollar_ex_gst')
            : this.translationService.get('lbl_debit_dollar'),
          pinnedRowCellRenderer: innerLabelCellRendererDecorator(this.translations.get('lbl_debit'))(
            defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
          cellClassRules: {
            [CELL_TEXT_RIGHT_OVERFLOW_CLASS]: ({ data }: { data: JobCoElementOrTotal }) => isJobCoElementTotal(data),
          },
          minWidth: 140,
        },
        'customerPrice' : {
          valueGetter: ({ data }: { data: JobCoElementTotal }) => {
            if(data.priceWithGst) {
              return data.priceWithGst;
            }
            if(isJobCoElementTotal(data)) {
              return data.variationTotalPriceWithGST;
            }
          },
          ...buildNumberColumn(NumberTypes.Price),
          pinnedRowCellRenderer: defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price)),
          cellClass: 'text-right',
          minWidth: 140,
        }, 
        'credit': {
          valueGetter: ({ data }: { data: JobCoElementOrTotal }) =>
            isJobCoElementTotal(data) ? data.credit : (data.isCredit ? data.priceExtension : void 0),
          ...buildNumberColumn(NumberTypes.Price),
          headerName: this.isAuOrNzRegion
            ? this.translationService.get('lbl_credit_dollar_ex_gst')
            : this.translationService.get('lbl_credit_dollar'),
          pinnedRowCellRenderer: innerLabelCellRendererDecorator(this.translations.get('lbl_credit'))(
            defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.Price))),
          cellClassRules: {
            'warn-color': (params: ValueFormatterParams) => isJobCoElement(params.data),
          },
          minWidth: 140,
        },
        'isInPriceBook': {
          cellRenderer: falsyCellRenderer(rendererIconByName('close')),
          pinnedRowCellRenderer: noneCellRenderer,
        },
        'hasMemo': {
          valueGetter: ({ data }: { data: JobCoElement }) => isNotNil(data.memo) && data.memo.length > 0,
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
      },
    });
  }

  private moveChanges(from: JobCoElement, to: JobCoElement): void {
    const { _elements } = this;

    const fromIndex = jobCoElementArr.indexOf(_elements, getJobCoElementId(from));
    const toIndex = jobCoElementArr.indexOf(_elements, getJobCoElementId(to));

    this.dragChanges.emit(jobCoElementArr.move(_elements, fromIndex, toIndex));
  }
}
